import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Paper,
  Card,
  CardContent,
  Stack,
  Chip,
  Fade,
  Zoom,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  Timeline as TimelineIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import CampaignTimelineEntry from './campaignTimelineEntry';
import CampaignTimelineCard from './campaignTimelineCard';
import useAlertDialog from '../../../../Components/useAlertDialog';
import AlertDialog from '../../../../Components/AlertDialog';

// IMPORT the MasterTimelinePopup
import MasterTimelinePopup from './masterpopup';
import { toast } from 'sonner';

const Timelines = ({
  campaignDetails,
  selectedCreatorsData,
  creatorTimelines,
  setCreatorTimelines,
  onNext,
  onBack,
}) => {
  const [masterTimeline, setMasterTimeline] = useState([]);
  const [expandedCreators, setExpandedCreators] = useState({});
  const [openDialog, setOpenDialog] = useState(false);

  // Add state for showing/hiding the Master Timeline Popup
  const [openMasterPopup, setOpenMasterPopup] = useState(true);

  const { dialogState, openDialog: openAlert, closeDialog: closeAlert } = useAlertDialog();

  // Existing logic for adding inline Master Timeline events
  const handleAddMasterEvent = () => {
    setMasterTimeline((prev) => [
      ...prev,
      {
        id: Date.now(),
        objective: '',
        status: 'incomplete',
        notes: '',
        manager_notes: '',
        manager_completed: false,
        projected_date: new Date(),
        will_delete: false,
      },
    ].sort((a, b) => new Date(a.projected_date) - new Date(b.projected_date)));
  };

  const handleEditMasterEvent = (index, field, value) => {
    setMasterTimeline((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated.sort((a, b) => new Date(a.projected_date) - new Date(b.projected_date));
    });
  };

  const handleDeleteMasterEvent = (index) => {
    setMasterTimeline((prev) => prev.filter((_, i) => i !== index));
  };

  // Apply Master Timeline events to all creators
  const applyMasterTimelineToAll = () => {
    toast.info('Applying master timeline', {
      description: 'Updating campaign schedule'
    });
    const newCreatorTimelines = {};

    selectedCreatorsData.forEach((creator) => {
      newCreatorTimelines[creator.creator] = masterTimeline.map((event) => ({
        ...event,
        id: Date.now() + Math.random(),
      }));
    });

    setCreatorTimelines(newCreatorTimelines);
    setOpenDialog(false);
  };

  const handleExpandClick = (creatorId) => {
    setExpandedCreators((prev) => ({
      ...prev,
      [creatorId]: !prev[creatorId],
    }));
  };

  // Add event for a single creator
  const handleAddCreatorEvent = (creatorId) => {
    setCreatorTimelines((prev) => ({
      ...prev,
      [creatorId]: [
        ...(prev[creatorId] || []),
        {
          id: Date.now(),
          objective: '',
          status: 'incomplete',
          notes: '',
          manager_notes: '',
          manager_completed: false,
          projected_date: new Date(),
          will_delete: false,
        },
      ].sort((a, b) => new Date(a.projected_date) - new Date(b.projected_date)),
    }));
  };

  // Edit event for a single creator
  const handleEditCreatorEvent = (creatorId, index, field, value) => {
    setCreatorTimelines((prev) => {
      const updatedEvents = [...(prev[creatorId] || [])];
      updatedEvents[index] = { ...updatedEvents[index], [field]: value };
      return {
        ...prev,
        [creatorId]: updatedEvents.sort((a, b) => new Date(a.projected_date) - new Date(b.projected_date)),
      };
    });
  };

  // Delete event for a single creator
  const handleDeleteCreatorEvent = (creatorId, index) => {
    setCreatorTimelines((prev) => {
      const updatedEvents = [...(prev[creatorId] || [])];
      updatedEvents.splice(index, 1);
      return {
        ...prev,
        [creatorId]: updatedEvents,
      };
    });
  };

  // Validation before going to next step
  const onTryNext = () => {
    const todayDate = new Date();
    // Check if any event is due before today
    const badDates = selectedCreatorsData.some((creator) => {
      const timeline = creatorTimelines[creator.creator];
      return timeline?.some((event) => event.projected_date <= todayDate);
    });

    if (badDates) {
      openAlert(
        'Incorrect Due Dates',
        <>Events in a Timeline can't be due before today.<br />Please adjust your Timelines to continue</>,
        closeAlert,
        closeAlert,
        'Ok',
        null
      );
      return;
    }

    // Check if any timeline is empty
    const anyBadData = selectedCreatorsData.some((creator) => {
      const timeline = creatorTimelines[creator.creator];
      return !timeline || timeline.length === 0;
    });

    if (anyBadData) {
      openAlert(
        'Empty To Do',
        <>Some creators in the campaign don't have any events in their to do items. <br />
           To Do List notify creators of upcoming deadlines and notify you when they are completed. Continue?</>,
        handleNext,
        closeAlert,
        'Continue',
        'Cancel'
      );
      return;
    }

    handleNext();
  };

  const handleNext = () => {
    onNext();
  };

  const totalEvents = masterTimeline.length + Object.values(creatorTimelines).flat().length;

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated Background Elements */}
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        zIndex: 0,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '15%',
          right: '20%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'float 8s ease-in-out infinite'
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '20%',
          left: '15%',
          width: '250px',
          height: '250px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'float 6s ease-in-out infinite reverse'
        },
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0px) scale(1)' },
          '50%': { transform: 'translateY(-15px) scale(1.05)' }
        }
      }} />

      <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Fade in timeout={800}>
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography 
              variant="h3" 
              sx={{ 
                color: 'white',
                fontWeight: 800,
                mb: 1,
                textShadow: '0 4px 20px rgba(0,0,0,0.3)',
                fontSize: { xs: '2.5rem', md: '3.5rem' }
              }}
            >
              Campaign Timeline
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'rgba(255,255,255,0.8)',
                fontWeight: 300,
                mb: 2,
                fontSize: { xs: '1rem', md: '1.25rem' }
              }}
            >
              Organize milestones and deliverables for your campaign
            </Typography>
            
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
              <Chip 
                icon={<TimelineIcon />} 
                label={`${totalEvents} Total Events`} 
                sx={{ 
                  backgroundColor: 'rgba(255,255,255,0.2)', 
                  color: 'white', 
                  fontWeight: 600,
                  backdropFilter: 'blur(10px)'
                }} 
              />
              <Chip 
                icon={<GroupIcon />} 
                label={`${selectedCreatorsData.length} Creators`} 
                sx={{ 
                  backgroundColor: 'rgba(255,255,255,0.2)', 
                  color: 'white', 
                  fontWeight: 600,
                  backdropFilter: 'blur(10px)'
                }} 
              />
            </Stack>
          </Box>
        </Fade>

        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          maxWidth: 1200,
          mx: 'auto'
        }}>
          {/* MasterTimelinePopup */}
          <MasterTimelinePopup
            open={openMasterPopup}
            onClose={() => setOpenMasterPopup(false)}
            masterTimeline={masterTimeline}
            setMasterTimeline={setMasterTimeline}
            onApplyToAllCreators={(updatedTimeline) => {
              setMasterTimeline(updatedTimeline);
              const newCreatorTimelines = {};
              selectedCreatorsData.forEach((creator) => {
                newCreatorTimelines[creator.creator] = updatedTimeline.map((event) => ({
                  ...event,
                  id: Date.now() + Math.random(),
                }));
              });
              setCreatorTimelines(newCreatorTimelines);
              setOpenMasterPopup(false);
            }}
          />

          {/* Master Timeline Section */}
          <Zoom in timeout={600}>
            <Card sx={{
              width: '100%',
              mb: 4,
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              overflow: 'visible',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '16px 16px 0 0'
              }
            }}>
              <CardContent sx={{ p: 4 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                  <Avatar sx={{
                    width: 56,
                    height: 56,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)'
                  }}>
                    <AutoAwesomeIcon sx={{ fontSize: 28, color: 'white' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight={800} color="#2c3e50">
                      Master Timeline
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Create a template timeline to apply to all creators
                    </Typography>
                  </Box>
                  <Box sx={{ ml: 'auto' }}>
                    <Tooltip title="Open Smart Timeline Builder">
                      <IconButton
                        onClick={() => setOpenMasterPopup(true)}
                        sx={{
                          backgroundColor: 'rgba(103, 126, 234, 0.1)',
                          color: '#667eea',
                          '&:hover': {
                            backgroundColor: 'rgba(103, 126, 234, 0.2)',
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Stack>

                {masterTimeline.length === 0 ? (
                  <Paper sx={{ 
                    p: 4, 
                    textAlign: 'center',
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #f8faff 0%, #f1f5ff 100%)',
                    border: '1px solid rgba(103, 126, 234, 0.1)'
                  }}>
                    <ScheduleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No master timeline events yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Create a master timeline to standardize milestones across all creators
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AutoAwesomeIcon />}
                      onClick={() => setOpenMasterPopup(true)}
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: 3,
                        fontWeight: 600,
                        px: 3,
                        py: 1
                      }}
                    >
                      Open Smart Builder
                    </Button>
                  </Paper>
                ) : (
                  <Stack spacing={2}>
                    {masterTimeline.map((event, index) => (
                      <CampaignTimelineEntry
                        key={event.id}
                        event={event}
                        onEditField={(field, value) => handleEditMasterEvent(index, field, value)}
                        onDelete={() => handleDeleteMasterEvent(index)}
                      />
                    ))}
                  </Stack>
                )}

                {/* Master Timeline Actions */}
                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  spacing={2} 
                  sx={{ mt: 3 }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddMasterEvent}
                    sx={{
                      borderColor: 'rgba(103, 126, 234, 0.3)',
                      color: '#667eea',
                      fontWeight: 600,
                      borderRadius: 3,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#667eea',
                        backgroundColor: 'rgba(103, 126, 234, 0.05)',
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    Add Milestone
                  </Button>
                  
                  {masterTimeline.length > 0 && (
                    <Button
                      variant="contained"
                      startIcon={<GroupIcon />}
                      onClick={() => setOpenDialog(true)}
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: 3,
                        fontWeight: 600,
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)'
                        }
                      }}
                    >
                      Apply to All Creators
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Zoom>

          {/* Creators Timelines Section */}
          <Fade in timeout={1000}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography 
                  variant="h4" 
                  fontWeight={800} 
                  sx={{ 
                    color: 'white',
                    textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                    mb: 1
                  }}
                >
                  Creator Timelines
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: 'rgba(255,255,255,0.8)',
                    fontWeight: 300
                  }}
                >
                  Individual deliverable schedules for each creator
                </Typography>
              </Box>

              <Stack spacing={3}>
                {selectedCreatorsData.map((creator, index) => (
                  <Zoom in timeout={1200 + index * 100} key={creator.creator}>
                    <div>
                      <CampaignTimelineCard
                        creator={creator}
                        expandedCreators={expandedCreators}
                        handleExpandClick={handleExpandClick}
                        handleAddCreatorEvent={handleAddCreatorEvent}
                        handleDeleteCreatorEvent={handleDeleteCreatorEvent}
                        handleEditCreatorEvent={handleEditCreatorEvent}
                        setCreatorTimelines={setCreatorTimelines}
                        creatorTimelines={creatorTimelines}
                        masterTimeline={masterTimeline}
                      />
                    </div>
                  </Zoom>
                ))}
              </Stack>
            </Box>
          </Fade>

          {/* Navigation */}
          <Fade in timeout={1400}>
            <Card sx={{
              mt: 4,
              width: '100%',
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 15px 35px rgba(0,0,0,0.1)'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  justifyContent="space-between" 
                  alignItems="center"
                  spacing={2}
                >
                  <Button 
                    variant="outlined"
                    onClick={onBack}
                    size="large"
                    startIcon={<ArrowBackIcon />}
                    sx={{ 
                      borderRadius: 3,
                      borderColor: 'rgba(103, 126, 234, 0.3)',
                      color: '#667eea',
                      fontWeight: 600,
                      py: 1.5,
                      px: 4,
                      minWidth: { xs: '100%', sm: 160 },
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: '#667eea',
                        backgroundColor: 'rgba(103, 126, 234, 0.05)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 10px 25px rgba(103, 126, 234, 0.15)'
                      }
                    }}
                  >
                    Previous
                  </Button>

                  <Box sx={{ textAlign: 'center', flex: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Review your timeline setup and continue
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    onClick={onTryNext}
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ 
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      fontWeight: 700,
                      py: 1.5,
                      px: 4,
                      fontSize: '1.1rem',
                      minWidth: { xs: '100%', sm: 160 },
                      boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 15px 35px rgba(102, 126, 234, 0.4)'
                      }
                    }}
                  >
                    Continue
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Fade>
        </Box>
      </Box>

      {/* Apply Master Timeline Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: '16px 16px 0 0',
          p: 3
        }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
              <GroupIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Apply Master Timeline
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                This will update all creator timelines
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <DialogContentText sx={{ fontSize: '1rem', color: '#2c3e50' }}>
            This will overwrite any existing timeline events for all creators with the master timeline. 
            Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setOpenDialog(false)}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={applyMasterTimelineToAll}
            variant="contained"
            sx={{
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              fontWeight: 600
            }}
          >
            Apply to All
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog alertState={dialogState} />
    </Box>
  );
};

export default Timelines;